import React, { useState, useEffect } from 'react';
import { TrustedAdvisorService } from '../../lib/trustedAdvisorService';
import { DocumentType, PermissionType, ViewMode } from '../../types/trustedAdvisor';
import { Card } from '../common/Card';
import { Badge } from '../common/Badge';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { 
  FileText, 
  Users, 
  User, 
  Calendar,
  Eye,
  Edit,
  Lock,
  Unlock,
  Share2,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  Clock,
  Mail
} from 'lucide-react';

interface SharedDocument {
  id: string;
  document_id: string;
  document_name: string;
  document_type: DocumentType;
  shared_by: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  permission_type: PermissionType;
  view_mode: ViewMode;
  shared_at: string;
  is_active: boolean;
}

interface SharedWithMeProps {
  onBack?: () => void;
}

const SharedWithMe: React.FC<SharedWithMeProps> = ({ onBack }) => {
  const [sharedDocuments, setSharedDocuments] = useState<SharedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<{
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | 'all'>('all');

  useEffect(() => {
    loadSharedDocuments();
  }, []);

  const loadSharedDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const documents = await TrustedAdvisorService.getSharedDocumentsWithMe();
      setSharedDocuments(documents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shared documents');
    } finally {
      setLoading(false);
    }
  };

  const getDocumentTypeLabel = (type: DocumentType) => {
    switch (type) {
      case 'will':
        return 'Will';
      case 'trust':
        return 'Trust';
      case 'power_of_attorney':
        return 'Power of Attorney';
      case 'health_directive':
        return 'Health Directive';
      case 'insurance':
        return 'Insurance';
      case 'financial_account':
        return 'Financial Account';
      case 'investment':
        return 'Investment';
      case 'retirement':
        return 'Retirement';
      case 'medical_record':
        return 'Medical Record';
      case 'appointment':
        return 'Appointment';
      case 'medication':
        return 'Medication';
      case 'pet_profile':
        return 'Pet Profile';
      case 'legacy_item':
        return 'Legacy Item';
      case 'digital_asset':
        return 'Digital Asset';
      default:
        return type;
    }
  };

  const getPermissionTypeLabel = (type: PermissionType) => {
    switch (type) {
      case 'share_now':
        return 'Share Now';
      case 'share_after_death':
        return 'Share After Death';
      case 'do_not_share':
        return 'Do Not Share';
      default:
        return type;
    }
  };

  const getViewModeLabel = (mode: ViewMode) => {
    switch (mode) {
      case 'view_only':
        return 'View Only';
      case 'view_and_comment':
        return 'View & Comment';
      case 'view_and_edit':
        return 'View & Edit';
      case 'full_access':
        return 'Full Access';
      default:
        return mode;
    }
  };

  const getPermissionTypeColor = (type: PermissionType) => {
    switch (type) {
      case 'share_now':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'share_after_death':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'do_not_share':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getViewModeColor = (mode: ViewMode) => {
    switch (mode) {
      case 'view_only':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'view_and_comment':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'view_and_edit':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'full_access':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Group documents by person who shared them
  const documentsByPerson = sharedDocuments.reduce((acc, doc) => {
    const personKey = doc.shared_by.id;
    if (!acc[personKey]) {
      acc[personKey] = {
        person: doc.shared_by,
        documents: []
      };
    }
    acc[personKey].documents.push(doc);
    return acc;
  }, {} as Record<string, { person: SharedDocument['shared_by']; documents: SharedDocument[] }>);

  const filteredDocumentsByPerson = Object.values(documentsByPerson).filter(({ person, documents }) => {
    const matchesSearch = searchTerm === '' || 
      person.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      person.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = selectedDocumentType === 'all' || 
      documents.some(doc => doc.document_type === selectedDocumentType);
    
    return matchesSearch && matchesType;
  });

  const selectedPersonDocuments = selectedPerson 
    ? documentsByPerson[selectedPerson.id]?.documents.filter(doc => {
        const matchesType = selectedDocumentType === 'all' || doc.document_type === selectedDocumentType;
        const matchesSearch = searchTerm === '' || 
          doc.document_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getDocumentTypeLabel(doc.document_type).toLowerCase().includes(searchTerm.toLowerCase());
        return matchesType && matchesSearch;
      }) || []
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <AlertCircle className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold mb-2">Error Loading Shared Documents</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadSharedDocuments} variant="primary">
            Try Again
          </Button>
        </div>
      </Card>
    );
  }

  if (sharedDocuments.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Shared With Me</h2>
            <p className="text-gray-600 mt-1">
              Documents shared with you as a trusted advisor
            </p>
          </div>
          {onBack && (
            <Button onClick={onBack} variant="outline" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
        </div>

        <Card className="p-6">
          <div className="text-center">
            <Share2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Shared Documents</h3>
            <p className="text-gray-600 mb-4">
              You don't have any documents shared with you yet. When someone adds you as a trusted advisor and shares documents, they will appear here.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Shared With Me</h2>
          <p className="text-gray-600 mt-1">
            Documents shared with you as a trusted advisor
          </p>
        </div>
        {onBack && (
          <Button onClick={onBack} variant="outline" className="flex items-center">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              placeholder="Search by name, email, or document..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Document Type
            </label>
            <select
              value={selectedDocumentType}
              onChange={(e) => setSelectedDocumentType(e.target.value as DocumentType | 'all')}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Document Types</option>
              <option value="will">Wills</option>
              <option value="trust">Trusts</option>
              <option value="power_of_attorney">Power of Attorney</option>
              <option value="health_directive">Health Directives</option>
              <option value="insurance">Insurance</option>
              <option value="financial_account">Financial Accounts</option>
              <option value="investment">Investments</option>
              <option value="retirement">Retirement</option>
              <option value="medical_record">Medical Records</option>
              <option value="digital_asset">Digital Assets</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Content */}
      {selectedPerson ? (
        // Show documents for selected person
        <div>
          <div className="flex items-center mb-6">
            <Button 
              onClick={() => setSelectedPerson(null)} 
              variant="outline" 
              className="flex items-center mr-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to People
            </Button>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {selectedPerson.first_name} {selectedPerson.last_name}
              </h3>
              <p className="text-gray-600">{selectedPerson.email}</p>
            </div>
          </div>

          {selectedPersonDocuments.length === 0 ? (
            <Card className="p-6">
              <div className="text-center">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Documents Found</h3>
                <p className="text-gray-600">
                  No documents match your current filters.
                </p>
              </div>
            </Card>
          ) : (
            <div className="grid gap-4">
              {selectedPersonDocuments.map((doc) => (
                <Card key={doc.id} className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <h4 className="text-lg font-medium text-gray-900">
                          {doc.document_name}
                        </h4>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <span>{getDocumentTypeLabel(doc.document_type)}</span>
                        <span>•</span>
                        <span>Shared {new Date(doc.shared_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-2">
                        <Badge 
                          variant={doc.permission_type === 'do_not_share' ? 'secondary' : 'primary'}
                          className={getPermissionTypeColor(doc.permission_type)}
                        >
                          {getPermissionTypeLabel(doc.permission_type)}
                        </Badge>
                        <Badge 
                          variant="outline"
                          className={getViewModeColor(doc.view_mode)}
                        >
                          {getViewModeLabel(doc.view_mode)}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {doc.permission_type === 'share_after_death' && (
                        <Badge variant="warning" className="flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          After Death
                        </Badge>
                      )}
                      {doc.permission_type === 'share_now' && (
                        <Badge variant="success" className="flex items-center">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Available Now
                        </Badge>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      ) : (
        // Show list of people who have shared documents
        <div>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              People Who Have Shared With You
            </h3>
            <p className="text-gray-600">
              Click on a person to view their shared documents
            </p>
          </div>

          {filteredDocumentsByPerson.length === 0 ? (
            <Card className="p-6">
              <div className="text-center">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No People Found</h3>
                <p className="text-gray-600">
                  No people match your current search criteria.
                </p>
              </div>
            </Card>
          ) : (
            <div className="grid gap-4">
              {filteredDocumentsByPerson.map(({ person, documents }) => (
                <Card 
                  key={person.id} 
                  className="p-6 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => setSelectedPerson(person)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-idecide-primary rounded-full flex items-center justify-center text-white font-semibold">
                        {person.first_name.charAt(0)}{person.last_name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="text-lg font-medium text-gray-900">
                          {person.first_name} {person.last_name}
                        </h4>
                        <p className="text-gray-600">{person.email}</p>
                        <p className="text-sm text-gray-500">
                          {documents.length} document{documents.length !== 1 ? 's' : ''} shared
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="primary">
                        {documents.filter(d => d.permission_type === 'share_now').length} Available Now
                      </Badge>
                      <Badge variant="warning">
                        {documents.filter(d => d.permission_type === 'share_after_death').length} After Death
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SharedWithMe; 